import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Phone, User, Upload, Camera, Crop, Check } from 'lucide-react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useAuth } from '../../contexts/AuthContext';
import { TrainerAPI } from '../../api/TrainerAPI';

const ProfileEditModal = ({ isOpen, onClose, profile, onSave }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    instructor_bank_account: '',
    instructor_pan: '',
    instructor_upi_id: '',
    photo: '',
  });

  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Image cropping states
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState('');
  const [crop, setCrop] = useState({
    unit: '%',
    width: 80,
    height: 80,
    x: 10,
    y: 10
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const { token } = useAuth();

  // 🟢 Load form values from profile when modal opens
  useEffect(() => {
    if (isOpen && profile) {
      const nameParts = profile.name?.split(' ') || [];
      setFormData({
        firstName: nameParts[0] || '',
        lastName: nameParts[1] || '',
        phone: profile.contact || '',
        instructor_bank_account: profile.instructor_bank_account || '',
        instructor_pan: profile.instructor_pan || '',
        instructor_upi_id: profile.instructor_upi_id || '',
        photo: profile.profile_image_url || '',
      });
      setImagePreview(profile.profile_image_url || '');
      setSelectedImageFile(null);
    }
  }, [isOpen, profile]);

  // Function to create cropped image
  const getCroppedImg = (image, crop) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop.width * pixelRatio * scaleX;
    canvas.height = crop.height * pixelRatio * scaleY;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error('Canvas is empty');
            return;
          }
          resolve(blob);
        },
        'image/jpeg',
        0.8
      );
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    // Create preview URL for cropping
    const reader = new FileReader();
    reader.onload = (event) => {
      setCropImageSrc(event.target.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
    
    setSelectedImageFile(file);
  };

  const handleCropComplete = async () => {
    if (!completedCrop || !imgRef.current) {
      alert('Please select a crop area');
      return;
    }

    try {
      const croppedImageBlob = await getCroppedImg(imgRef.current, completedCrop);
      
      // Create a new File object from the blob
      const croppedFile = new File([croppedImageBlob], 'cropped-image.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      // Create preview URL
      const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
      setImagePreview(croppedImageUrl);
      setSelectedImageFile(croppedFile);
      
      // Close crop modal
      setShowCropModal(false);
      setCropImageSrc('');
    } catch (error) {
      console.error('Error cropping image:', error);
      alert('Failed to crop image. Please try again.');
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setCropImageSrc('');
    setSelectedImageFile(null);
    // Reset file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let finalImageUrl = formData.photo; // Keep existing image by default

      // ✅ 1. Upload image to S3 ONLY if a new file is selected
      if (selectedImageFile) {
        setImageUploading(true);
        try {
          const uploadResult = await TrainerAPI.uploadProfileImage(selectedImageFile);
          finalImageUrl = uploadResult.imageUrl;
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          alert('Failed to upload image: ' + uploadError.message);
          return; // Stop execution if image upload fails
        } finally {
          setImageUploading(false);
        }
      }

      // ✅ 2. Always update profile (with or without new image)
      const profileUpdateData = {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        contact: formData.phone,
        instructor_bank_account: formData.instructor_bank_account,
        instructor_pan: formData.instructor_pan,
        instructor_upi_id: formData.instructor_upi_id,
        profile_image_url: finalImageUrl, // Include image URL (existing or newly uploaded)
      };

      // 3. Call the UPDATE PROFILE API (not upload API)
      await TrainerAPI.updateTrainerProfile(profileUpdateData);
      
      // 4. Success handling
      alert('Profile updated successfully!');
      await onSave(); // Refresh parent component
      onClose(); // Close modal
      
    } catch (error) {
      console.error('Profile update error:', error);
      alert(error.message || 'Failed to save profile');
    } finally {
      setLoading(false);
      setImageUploading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      await TrainerAPI.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      alert('Password updated successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowResetPassword(false);
    } catch (error) {
      alert(error.message || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Get initials for avatar fallback
  const getInitials = () => {
    const name = `${formData.firstName} ${formData.lastName}`.trim();
    if (!name) return 'U';
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-2xl font-bold text-gray-900">Edit Profile</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          {!showResetPassword ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Profile Image Upload Section */}
              <div className="flex flex-col items-center mb-8">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Profile Image
                </label>

                <div className="relative flex flex-col items-center">
                  <div className="w-24 h-24 rounded-full border-3 border-gray-300 overflow-hidden bg-gray-100 cursor-pointer hover:border-blue-400 transition-colors duration-200 group">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-[#FC7A69] to-[#221E45] text-white font-semibold text-lg">
                        {getInitials()}
                      </div>
                    )}

                    {/* Upload Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <Camera className="w-6 h-6 text-white" />
                    </div>

                    {/* Hidden File Input */}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={imageUploading}
                    />
                  </div>

                  {imageUploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 rounded-full">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  )}

                  <p className="text-xs text-gray-500 mt-2 text-center">
                    {imageUploading ? 'Uploading...' : 'Click to upload image (Max 5MB)'}
                  </p>
                </div>
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter First Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter Last Name"
                  />
                </div>
              </div>

              {/* Phone Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+91 12345 67890"
                  />
                </div>
              </div>

              {/* Financial Information */}
          {/* <div>
  <label className="block text-sm font-semibold text-gray-700 mb-2">
    Bank Details
  </label>

  <textarea
    value={formData.instructor_bank_account}
    onChange={(e) =>
      setFormData({
        ...formData,
        instructor_bank_account: e.target.value,
      })
    }
    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
    placeholder="Enter your bank account details..."
    rows={4}
  />
</div> */}
              {/* <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">UPI ID</label>
                <input
                  type="text"
                  value={formData.instructor_upi_id}
                  onChange={(e) => setFormData({ ...formData, instructor_upi_id: e.target.value })}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="user@paytm"
                />
              </div> */}

              {/* Action Buttons */}
              <div className="flex space-x-4 pt-6">
                <button
                  type="button"
                  onClick={() => setShowResetPassword(true)}
                  className="flex-1 px-6 py-3 border border-orange-300 rounded-lg text-orange-700 hover:bg-orange-50 font-medium transition-colors"
                >
                  Reset Password
                </button>
                <button
                  type="submit"
                  disabled={loading || imageUploading}
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center font-medium transition-colors"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {imageUploading ? 'Uploading Image...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Update Profile
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            // Password Reset Form
            <form onSubmit={handlePasswordReset} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, currentPassword: e.target.value })
                  }
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter current password"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, newPassword: e.target.value })
                  }
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter new password"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                  }
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Confirm new password"
                  required
                />
              </div>

              <div className="flex space-x-4 pt-6">
                <button
                  type="button"
                  onClick={() => setShowResetPassword(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="flex-1 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium transition-colors"
                >
                  {passwordLoading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Image Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-60">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <Crop className="w-6 h-6 mr-2" />
                  Crop Your Image
                </h3>
                <button 
                  onClick={handleCropCancel}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="flex flex-col items-center space-y-6">
                <div className="w-full max-w-2xl">
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={1}
                    circularCrop
                  >
                    <img
                      ref={imgRef}
                      alt="Crop me"
                      src={cropImageSrc}
                      style={{ maxHeight: '60vh', width: 'auto' }}
                      onLoad={(e) => {
                        const { width, height } = e.currentTarget;
                        setCrop({
                          unit: '%',
                          width: 80,
                          height: 80,
                          x: 10,
                          y: 10
                        });
                      }}
                    />
                  </ReactCrop>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={handleCropCancel}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCropComplete}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Apply Crop
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileEditModal;